/**
 * Processes the window.fandomHost.cmd queue and executes all callbacks with the newly
 * created fandomHost object. Then modifies the array push method to automatically call any new
 * callbacks immediately.
 *
 * To add a function to the command queue:
 *
 * ```ts
 * window.fandomHost = window.fandomHost || {}
 * window.fandomHost.cmd = window.fandomHost.cmd || []
 *
 * window.fandomHost.cmd.push((host: FandomHost) => {
 *   const { geo, app, skin, icbm } = host
 * })
 * ```
 * @param fandomHost The fandomHost object that contains the command queue and will be passed into
 * each callback
 */
type FandomObjectCommandCallback<T> = (fandomObject: T) => void;
type FandomObject<T> = {
  cmd?: FandomObjectCommandCallback<T>[];
};
export function processCommandQueue<T>(fandomObject: T & FandomObject<T>) {
  fandomObject.cmd ??= [];

  // Save the current state of the queue
  const callbacksQueue = [...fandomObject.cmd];

  // Modify push to execute new additions immediately
  fandomObject.cmd.push = (...callbacks: FandomObjectCommandCallback<T>[]) => {
    for (const callback of callbacks) {
      callback(fandomObject);
    }

    return 0;
  };

  // Process all the callbacks that were on the queue
  for (const callback of callbacksQueue) {
    callback(fandomObject);
  }
}
